import React, { useState } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAuth } from "../../hooks/useAuth";
import { useAlert } from "../../contexts/AlertContext";
import { transactionAPI } from "../../services/api";
import {
  MdAccountBalance,
  MdTrendingUp,
  MdCardGiftcard,
  MdSwapHoriz,
  MdSavings,
} from "react-icons/md";

// Define types at the top level
type BalanceType = "ledger" | "stake" | "bonus" | "earnings";

interface Balance {
  type: BalanceType;
  title: string;
  amount: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
}

interface TransferData {
  from: BalanceType;
  to: BalanceType;
  amount: string;
}

const Balances = () => {
  const { user, refreshUser } = useAuth();
  const { addAlert } = useAlert();
  const [transferData, setTransferData] = useState<TransferData>({
    from: "stake",
    to: "ledger",
    amount: "",
  });
  const [isTransferring, setIsTransferring] = useState(false);

  // Cast icons to proper React component type
  const BalanceIcon = MdAccountBalance as React.ComponentType<{
    className?: string;
  }>;
  const TrendIcon = MdTrendingUp as React.ComponentType<{ className?: string }>;
  const GiftIcon = MdCardGiftcard as React.ComponentType<{
    className?: string;
  }>;
  const SwapIcon = MdSwapHoriz as React.ComponentType<{ className?: string }>;
  const SavingsIcon = MdSavings as React.ComponentType<{ className?: string }>;

  // Helper function to safely convert balance values to numbers
  const getBalanceAmount = (
    balanceValue: string | number | null | undefined
  ): number => {
    if (balanceValue === null || balanceValue === undefined) return 0;
    return typeof balanceValue === "string"
      ? parseFloat(balanceValue)
      : balanceValue;
  };

  const balances: Balance[] = [
    {
      type: "ledger",
      title: "Ledger Balance",
      amount: getBalanceAmount(user?.ledger_balance),
      icon: BalanceIcon,
      color: "bg-blue-500",
      description: "Available for transfers and withdrawals",
    },
    {
      type: "stake",
      title: "Stake Balance",
      amount: getBalanceAmount(user?.stake_balance),
      icon: TrendIcon,
      color: "bg-green-500",
      description: "Available for trading and staking",
    },
    {
      type: "bonus",
      title: "Bonus Balance",
      amount: getBalanceAmount(user?.bonus_balance),
      icon: GiftIcon,
      color: "bg-purple-500",
      description: "Bonus funds with special conditions",
    },
    {
      type: "earnings",
      title: "Earnings Balance",
      amount: getBalanceAmount(user?.earnings_balance),
      icon: SavingsIcon,
      color: "bg-orange-500",
      description: "Your earnings from investments",
    },
  ];

  const handleTransfer = async () => {
    if (!transferData.amount || parseFloat(transferData.amount) <= 0) {
      addAlert({ type: "error", message: "Please enter a valid amount" });
      return;
    }

    const fromBalance =
      balances.find((b) => b.type === transferData.from)?.amount || 0;
    if (parseFloat(transferData.amount) > fromBalance) {
      addAlert({ type: "error", message: "Insufficient balance" });
      return;
    }

    // Prevent transfers to bonus or earnings
    if (["bonus", "earnings"].includes(transferData.to)) {
      addAlert({
        type: "error",
        message: "Cannot transfer to bonus or earnings balance",
      });
      return;
    }

    setIsTransferring(true);
    try {
      await transactionAPI.transferBalance({
        from: transferData.from,
        to: transferData.to,
        amount: parseFloat(transferData.amount),
      });

      addAlert({
        type: "success",
        message: "Transfer completed successfully!",
      });
      setTransferData((prev) => ({ ...prev, amount: "" }));
      await refreshUser(); // Refresh user data to get updated balances
    } catch (error: any) {
      console.error("Transfer error:", error);
      addAlert({
        type: "error",
        message:
          error.response?.data?.message || "Transfer failed. Please try again.",
      });
    } finally {
      setIsTransferring(false);
    }
  };

  // Helper function to safely convert string to BalanceType
  const toBalanceType = (value: string): BalanceType => {
    const validTypes: BalanceType[] = ["ledger", "stake", "bonus", "earnings"];
    return validTypes.includes(value as BalanceType)
      ? (value as BalanceType)
      : "ledger"; // default fallback
  };

  const getAvailableToBalances = (fromType: BalanceType): Balance[] => {
    // Can't transfer to bonus or earnings, and can't transfer to same balance
    return balances.filter(
      (balance) =>
        !["bonus", "earnings"].includes(balance.type) &&
        balance.type !== fromType
    );
  };

  return (
    <div className={GlobalStyles.container}>
      <div>
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
        >
          Account Balances
        </h1>
        <p className={`${CommonClasses.textGray}`}>
          Manage your account balances and transfer funds between different
          balance types.
        </p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {balances.map((balance, index) => {
          const Icon = balance.icon;
          return (
            <div
              key={index}
              className={`${GlobalStyles.sectionWrap} text-center p-6`}
            >
              <div
                className={`w-16 h-16 ${balance.color} rounded-full flex items-center justify-center mx-auto mb-4`}
              >
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h3
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-2`}
              >
                {balance.title}
              </h3>
              <p
                className={`${CommonClasses.textXl} font-bold text-primary mb-2`}
              >
                ${balance.amount.toFixed(2)}
              </p>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                {balance.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Balance Transfer */}
      <div className={`${GlobalStyles.sectionWrap}`}>
        <h2
          className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
        >
          Transfer Between Balances
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className={GlobalStyles.formGroup}>
            <label className={GlobalStyles.label}>From</label>
            <select
              value={transferData.from}
              onChange={(e) => {
                const newFrom = toBalanceType(e.target.value);
                setTransferData((prev) => ({
                  ...prev,
                  from: newFrom,
                  to: getAvailableToBalances(newFrom)[0]?.type || "ledger",
                }));
              }}
              className={GlobalStyles.input}
            >
              {balances.map((balance) => (
                <option key={balance.type} value={balance.type}>
                  {balance.title} (${balance.amount.toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-center">
            <SwapIcon className="w-6 h-6 text-gray-500" />
          </div>

          <div className={GlobalStyles.formGroup}>
            <label className={GlobalStyles.label}>To</label>
            <select
              value={transferData.to}
              onChange={(e) =>
                setTransferData((prev) => ({
                  ...prev,
                  to: toBalanceType(e.target.value),
                }))
              }
              className={GlobalStyles.input}
            >
              {getAvailableToBalances(transferData.from).map((balance) => (
                <option key={balance.type} value={balance.type}>
                  {balance.title}
                </option>
              ))}
            </select>
          </div>

          <div className={GlobalStyles.formGroup}>
            <label className={GlobalStyles.label}>Amount</label>
            <input
              type="number"
              value={transferData.amount}
              onChange={(e) =>
                setTransferData((prev) => ({ ...prev, amount: e.target.value }))
              }
              placeholder="0.00"
              className={GlobalStyles.input}
              max={
                balances.find((b) => b.type === transferData.from)?.amount || 0
              }
            />
          </div>

          <button
            onClick={handleTransfer}
            disabled={
              isTransferring ||
              !transferData.amount ||
              parseFloat(transferData.amount) <= 0
            }
            className={`${GlobalStyles.buttonPrimary} ${
              isTransferring || !transferData.amount
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            {isTransferring ? "Transferring..." : "Transfer"}
          </button>
        </div>
        <p className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-2`}>
          Note: Cannot transfer to Bonus or Earnings balances
        </p>
      </div>
    </div>
  );
};

export default Balances;
